import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'wouter';
import { compradorTrpc } from '../../lib/compradorTrpc';
import { AppShellComprador } from '../../components/comprador/AppShellComprador';
import { useCompradorSessao, sairParaLogin, rotaApp } from './util';

interface ArtigoCompleto {
  slug: string;
  categoria: string;
  titulo: string;
  corpo: string;
  minutosLeitura: number;
}

/**
 * Leitura do artigo (design aprovado). O corpo é texto simples: parágrafos
 * separados por linha em branco; linhas iniciadas com "## " viram subtítulos.
 * Renderização própria, sem HTML cru (conteúdo vem do banco, nunca injetado).
 */
export function ArtigoComprador() {
  const slug = useCompradorSessao();
  const { artigoSlug } = useParams<{ slug: string; artigoSlug: string }>();
  const [, navigate] = useLocation();
  const [corMarca, setCorMarca] = useState<string | null>(null);
  const [orgNome, setOrgNome] = useState('');
  const [artigo, setArtigo] = useState<ArtigoCompleto | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    if (!slug || !artigoSlug) return;
    let vivo = true;
    Promise.all([
      compradorTrpc.auth.me.query({ orgSlug: slug }),
      compradorTrpc.app.artigo.query({ orgSlug: slug, slug: artigoSlug }),
    ])
      .then(([m, a]) => {
        if (!vivo) return;
        setCorMarca(m.corMarca);
        setOrgNome(m.orgNome);
        setArtigo(a as ArtigoCompleto);
      })
      .catch(() => sairParaLogin(slug, navigate))
      .finally(() => vivo && setCarregando(false));
    return () => {
      vivo = false;
    };
  }, [slug, artigoSlug, navigate]);

  const blocos = (artigo?.corpo ?? '')
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean);

  return (
    <AppShellComprador corMarca={corMarca} orgNome={orgNome}>
      <div data-testid="artigo-comprador">
        <Link
          href={rotaApp(slug!, '/aprender')}
          aria-label="Voltar para Aprender"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-line bg-surface"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
            <path d="M14.5 6L9 12l5.5 6" stroke="var(--ink-2)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Link>

        {carregando ? (
          <p className="py-16 text-center text-sm text-ink-3">Carregando…</p>
        ) : !artigo ? (
          <p className="py-16 text-center text-sm text-ink-3">Conteúdo não encontrado.</p>
        ) : (
          <article className="mt-4">
            <div
              className="flex h-40 items-end rounded-3xl p-4"
              style={{ background: 'linear-gradient(150deg, var(--brand), var(--brand-deep))' }}
            >
              <span className="rounded-full bg-white/20 px-2.5 py-1 text-[11px] font-semibold text-white">
                {artigo.categoria}
              </span>
            </div>
            <h1 className="mt-5 font-serif text-[26px] font-medium leading-tight tracking-tight text-ink">
              {artigo.titulo}
            </h1>
            <div className="mt-2 text-xs text-ink-4">
              {artigo.minutosLeitura} min de leitura · {orgNome}
            </div>
            <div className="mt-5 space-y-4">
              {blocos.map((b, i) =>
                b.startsWith('## ') ? (
                  <h2 key={i} className="pt-2 font-serif text-[19px] font-medium text-ink">
                    {b.slice(3)}
                  </h2>
                ) : (
                  <p key={i} className="text-[15px] leading-relaxed text-ink-2">
                    {b}
                  </p>
                ),
              )}
            </div>
          </article>
        )}
      </div>
    </AppShellComprador>
  );
}
